import { useState } from 'react';
import { apiWrite } from '../../lib/api-client.js';

interface BulkAssignResult {
  results?: unknown[];
  errors?: string[];
  error?: string;
}

interface ConstraintResult {
  constraint?: { id: string | number; [key: string]: unknown };
  error?: string;
}

interface DeleteResult {
  deleted?: boolean;
  error?: string;
}

interface TimeOffRequestResult {
  request?: { id: string | number; [key: string]: unknown };
  error?: string;
}

interface DayOverrideResult {
  day_override?: { id: string | number; [key: string]: unknown };
  already_off?: boolean;
  error?: string;
}

export function useSchedulerWrite(onSuccess: () => void) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function bulkAssign(
    employeeIds: (string | number)[],
    terminalId: string | number | null,
    assignmentType: string,
    startDate: string,
    notes?: string,
  ): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<BulkAssignResult>('/api/scheduler/assign/bulk', 'POST', {
        employee_ids: employeeIds,
        terminal_id: terminalId,
        assignment_type: assignmentType,
        start_date: startDate,
        notes: notes || null,
      });
      if (!ok || data.error) {
        setError(data.error || 'Bulk assign failed');
        return false;
      }
      onSuccess();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function addConstraint(
    employeeId: string | number,
    constraintType: string,
    description: string,
    dayOfWeek?: number | null,
  ): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<ConstraintResult>('/api/scheduler/constraint', 'POST', {
        employee_id: employeeId,
        constraint_type: constraintType,
        description,
        day_of_week: dayOfWeek ?? null,
      });
      if (!ok || data.error) {
        setError(data.error || 'Failed to add constraint');
        return false;
      }
      onSuccess();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteConstraint(constraintId: string | number): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<DeleteResult>(`/api/scheduler/constraint/${constraintId}`, 'DELETE', {});
      if (!ok || data.error) {
        setError(data.error || 'Failed to delete constraint');
        return false;
      }
      onSuccess();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function requestTimeOff(
    employeeId: string | number,
    requestType: string,
    startDate: string,
    endDate: string,
    reason?: string,
  ): Promise<boolean> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<TimeOffRequestResult>('/api/scheduler/time-off/request', 'POST', {
        employee_id: employeeId,
        request_type: requestType,
        start_date: startDate,
        end_date: endDate,
        reason: reason || null,
      });
      if (!ok || data.error) {
        setError(data.error || 'Failed to submit time-off request');
        return false;
      }
      onSuccess();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function createDayOverride(
    employeeId: string | number,
    date: string,
    reason?: string,
  ): Promise<{ success: boolean; alreadyOff?: boolean }> {
    setSubmitting(true);
    setError(null);
    try {
      const { ok, data } = await apiWrite<DayOverrideResult>('/api/scheduler/day-override', 'POST', {
        employee_id: employeeId,
        date,
        reason: reason || null,
      });
      if (!ok || data.error) {
        setError(data.error || 'Failed to save day override');
        return { success: false };
      }
      onSuccess();
      return { success: true, alreadyOff: !!data.already_off };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return { success: false };
    } finally {
      setSubmitting(false);
    }
  }

  return {
    bulkAssign,
    addConstraint,
    deleteConstraint,
    requestTimeOff,
    createDayOverride,
    submitting,
    error,
    clearError: () => setError(null),
  };
}
